import { useSettlements } from '@services/settlements/queries/useSettlements'
import { Receipt } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { FlatDetail } from 'types/api'

import { SectionCard } from '@/features/settlements/components/SectionCard'
import {
  absoluteAmount,
  formatDate,
  isUnderpayment,
  money
} from '@/features/settlements/components/format'
import { cn } from '@/lib/utils'

import { FlatMeters } from './FlatMeters'
import { SettlementCreateDialog } from './SettlementCreateDialog'

export function FlatSettlements({ flat }: { flat: FlatDetail }) {
  const { t } = useTranslation('settlement')
  const { data, isLoading } = useSettlements({ flat: flat.id })

  return (
    <div className="space-y-6">
      <SectionCard
        title={t('title')}
        icon={Receipt}
        action={<SettlementCreateDialog flatId={flat.id!} />}
      >
        {isLoading ? (
          <div className="h-24 animate-pulse rounded-2xl bg-ink/[0.04]" />
        ) : !data || data.results.length === 0 ? (
          <div>
            <p className="m-0 font-medium text-ink">{t('empty.title')}</p>
            <p className="m-0 text-sm text-muted-foreground">
              {t('empty.subtitle')}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.results.map((settlement) => {
              const published = settlement.status === 'PUBLISHED'
              const total = settlement.grand_total
              const underpayment = isUnderpayment(total)

              return (
                <Link
                  key={settlement.id}
                  to={`/admin/settlements/${settlement.id}`}
                  className="flex flex-wrap items-center gap-3 rounded-xl border border-ink/[0.08] px-4 py-3 no-underline transition-colors hover:border-primary/40"
                >
                  <div className="min-w-0 flex-1">
                    <p className="m-0 font-medium text-ink">
                      {settlement.title}
                    </p>
                    <p className="m-0 text-sm text-muted-foreground">
                      {formatDate(settlement.period_start)} –{' '}
                      {formatDate(settlement.period_end)}
                    </p>
                  </div>

                  <span
                    className={cn(
                      'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                      published
                        ? 'bg-success-soft text-success'
                        : 'bg-ink/[0.06] text-muted-foreground'
                    )}
                  >
                    {t(`status.${settlement.status}`)}
                  </span>

                  {total !== null && (
                    <span
                      className={cn(
                        'text-sm font-semibold tabular-nums',
                        underpayment ? 'text-destructive' : 'text-success'
                      )}
                    >
                      {underpayment
                        ? t('result.underpayment')
                        : t('result.overpayment')}{' '}
                      {money(absoluteAmount(total))}
                    </span>
                  )}
                </Link>
              )
            })}
          </div>
        )}
      </SectionCard>

      <FlatMeters flatId={flat.id!} />
    </div>
  )
}
